/* eslint-disable import/no-default-export */
import classNames from 'classnames';
import React, { useState } from 'react';

import { TestId } from '@trello/webdriverio-tests';
// eslint-disable-next-line no-restricted-imports
import Icon, { IconColor, IconName } from 'common/components/controls/icon';
import styles from './header.less';

const useFocusControl = ({
  onClick: onClickExternal,
  onMouseEnter: onMouseEnterExternal,
}: {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  onMouseEnter?: React.MouseEventHandler;
}) => {
  const [hasFocusRing, setHasFocusRing] = useState(false);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [wasActivatedByMouse, setWasActivatedByMouse] = useState(false);
  const [wasActive, setWasActive] = useState(false);

  const enableFocusRing = () => {
    if (!isMouseOver && !wasActivatedByMouse) {
      setHasFocusRing(true);
    }

    setWasActivatedByMouse(false);
  };

  return [
    hasFocusRing,
    {
      onMouseEnter: (e: React.MouseEvent) => {
        setIsMouseOver(true);
        setHasFocusRing(false);

        if (onMouseEnterExternal) {
          onMouseEnterExternal(e);
        }
      },
      onMouseDown: () => {
        setWasActivatedByMouse(true);
        setHasFocusRing(false);
      },
      onMouseLeave: () => {
        setIsMouseOver(false);
      },
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
        const button = e.currentTarget;

        setImmediate(() => {
          if (button && !wasActive) {
            button.focus();
          }
        });

        onClickExternal(e);
      },
      onKeyDown: () => {
        setWasActive(true);
        enableFocusRing();
      },
      onKeyUp: () => {
        if (!wasActive) {
          enableFocusRing();
        }
      },
      onBlur: () => {
        setWasActive(false);
        setHasFocusRing(false);
      },
    },
  ];
};

interface HeaderButtonProps {
  ariaLabel?: string;
  children?: React.ReactNode;
  className?: string;
  icon?: IconName;
  iconColor?: IconColor;
  onClick: React.MouseEventHandler;
  onMouseEnter?: React.MouseEventHandler;
  testId?: TestId;
  title?: string;
}

const HeaderButton: React.RefForwardingComponent<
  HTMLButtonElement,
  HeaderButtonProps
> = (
  {
    ariaLabel,
    children,
    className,
    icon,
    iconColor,
    onClick: onClickExternal,
    onMouseEnter: onMouseEnterExternal,
    testId,
    title,
  },
  ref,
) => {
  const [hasFocusRing, events] = useFocusControl({
    onClick: onClickExternal,
    onMouseEnter: onMouseEnterExternal,
  });

  return (
    <button
      aria-label={ariaLabel}
      className={classNames(
        styles.headerButton,
        className,
        hasFocusRing && styles.focusRing,
      )}
      data-test-id={testId}
      ref={ref}
      title={title}
      {...events}
    >
      {icon && (
        <Icon
          color={iconColor}
          large
          name={icon}
          className={styles.headerButtonIcon}
        />
      )}
      {children}
    </button>
  );
};

export default React.forwardRef(HeaderButton);



// WEBPACK FOOTER //
// ./app/gamma/src/components/header/button.tsx